import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Step,
    StepLabel,
    Stepper,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
} from "@mui/material";
import { DataGrid, deDE } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { IOffer } from "../../../interfaces/IOffer";
import { IPartner } from "../../../interfaces/IPartner";
import { IProjectPartnerAllocation } from "../../../interfaces/IProjectPartnerAllocation";
import Transition from "../../core/Transition";

interface OfferAssignmentDialogProps {
    offers: IOffer[];
    allPartners: IPartner[];
    allocations: IProjectPartnerAllocation[] | undefined;

    onFinish?: (
        offer: IOffer,
        partners: IPartner[],
        overwriteAllocation: boolean,
        useExistingAllocation: boolean,
    ) => void;
    onAbort?: () => void;
}

const OfferAssignmentDialog: React.FC<OfferAssignmentDialogProps> = ({
    offers,
    allPartners,
    allocations,
    onFinish = () => {},
    onAbort = () => {},
}) => {
    const [selectedOffer, setSelectedOffer] = useState<IOffer | undefined>();
    const [selectedPartners, setSelectedPartners] = useState<IPartner[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const [allocationChoice, setAllocationChoice] = useState<string>("createNew");

    const steps = [{ name: "Angebot auswählen" }, { name: "Partner auswählen" }];

    const getAllocationForOffer = (offer: IOffer): IProjectPartnerAllocation[] | undefined =>
        allocations?.filter((allocation) =>
            offer.OfferPositions.some((position) => allocation.positions.includes(position.id)),
        );

    const allocationExists = selectedOffer ? (getAllocationForOffer(selectedOffer)?.length ?? 0) > 0 : false;

    useEffect(() => {
        if (!selectedOffer) {
            setActiveStep(0);
            setAllocationChoice("createNew");
        }
    }, [selectedOffer]);

    return (
        <Dialog open fullWidth maxWidth="md" TransitionComponent={Transition} onClose={onAbort}>
            <Stepper sx={{ pt: 2, px: 2 }} activeStep={activeStep}>
                {steps.map(({ name }) => (
                    <Step key={name}>
                        <StepLabel>{name}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && (
                <>
                    <DialogTitle>Für welches Angebot soll eine Handwerkerbeauftragung erstellt werden?</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            {offers.length === 0 ? (
                                <Typography variant="body1">Keine Angebote vorhanden.</Typography>
                            ) : (
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Angebot auswählen"
                                    select
                                    size="small"
                                    value={selectedOffer?.id || ""}
                                    onChange={(e) => {
                                        const offerId = Number(e.target.value);
                                        const offer = offers.find((o) => o.id === offerId);
                                        setSelectedOffer(offer);
                                        setAllocationChoice("createNew");
                                    }}
                                    error={!selectedOffer}
                                    helperText={!selectedOffer ? "Bitte wählen Sie ein Angebot aus." : ""}
                                >
                                    {offers.map((offer) => (
                                        <MenuItem key={offer.id} value={offer.id}>
                                            {offer.title}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Box>

                        {selectedOffer && allocationExists && (
                            <Box sx={{ mt: 2 }}>
                                <Typography>
                                    Für dieses Angebot gibt es bereits eine aktive Zuordnung. Wie möchten Sie
                                    fortfahren?
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        value={allocationChoice ?? ""}
                                        onChange={(e) => setAllocationChoice(e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="useExisting"
                                            control={<Radio />}
                                            label="Bestehende Zuordnung verwenden"
                                        />
                                        <FormControlLabel
                                            value="manualAssign"
                                            control={<Radio />}
                                            label="Manuell zuordnen"
                                        />
                                        <FormControlLabel
                                            value="createNew"
                                            control={<Radio />}
                                            label="Neu zuordnen (bestehende überschreiben)"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        )}

                        {selectedOffer && !allocationExists && (
                            <Typography sx={{ mt: 2 }}>
                                Für dieses Angebot existiert noch keine Zuordnung. Bitte wählen Sie Partner aus.
                            </Typography>
                        )}
                    </DialogContent>
                </>
            )}

            {activeStep === 1 && (
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        {allocationExists && allocationChoice === "createNew"
                            ? "Wählen Sie die Partner aus, die zugeordnet werden sollen. Die bestehende Zuordnung wird überschrieben."
                            : "Wählen Sie die Partner aus, die für dieses Angebot zugeordnet werden sollen."}
                    </Typography>

                    <DataGrid
                        autoHeight
                        checkboxSelection
                        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                        rows={allPartners}
                        columns={[
                            { field: "id", headerName: "ID", width: 90 },
                            {
                                field: "Contact_Person.Company.company_name",
                                headerName: "Partner",
                                flex: 1,
                                renderCell: ({ row }) =>
                                    row.Contact_Person.Company?.company_name ??
                                    `${row.Contact_Person.firstname} ${row.Contact_Person.lastname}`,
                            },
                        ]}
                        onRowSelectionModelChange={(selection) =>
                            setSelectedPartners(selection.map((id) => allPartners.find((p) => p.id === id)!))
                        }
                        rowSelectionModel={selectedPartners.map((p) => p.id)}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 50 } },
                        }}
                    />
                </DialogContent>
            )}

            <DialogActions>
                <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={activeStep === 0}>
                    Zurück
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedOffer || (allocationExists && !allocationChoice)}
                    onClick={() => {
                        if (activeStep === 0) {
                            if (allocationExists && allocationChoice === "useExisting") {
                                const existingPartners = allPartners.filter((partner) =>
                                    getAllocationForOffer(selectedOffer!)!.some((a) => a.Partner === partner.id),
                                );
                                onFinish(selectedOffer!, existingPartners, false, true);
                            } else {
                                setActiveStep(1);
                                if (allocationExists && allocationChoice === "manualAssign") {
                                    const existingPartners = allPartners.filter((partner) =>
                                        getAllocationForOffer(selectedOffer!)!.some((a) => a.Partner === partner.id),
                                    );
                                    setSelectedPartners(existingPartners);
                                } else {
                                    setSelectedPartners([]);
                                }
                            }
                        } else {
                            console.log(allocationChoice);
                            const overwrite = allocationChoice === "createNew";
                            console.log(overwrite);
                            onFinish(selectedOffer!, selectedPartners, overwrite, false);
                        }
                    }}
                >
                    {activeStep === 0 ? "Weiter" : "Fertig"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OfferAssignmentDialog;
